import { useState, useCallback } from 'react';
import { useLoader } from '../contexts/LoaderContext';
import { useAlert } from '../contexts/AlertContext';
import { events } from '../api/events';
import { Event } from '../types/event';

interface UseEventsApiProps {
  itemsPerPage?: number;
  startDate?: Date | null;
  endDate?: Date | null;
}

interface EventsData {
  events: Event[];
  totalPages: number;
}

export const useEventsApi = ({ itemsPerPage = 10, startDate, endDate }: UseEventsApiProps = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsData, setEventsData] = useState<EventsData>({
    events: [],
    totalPages: 0,
  });

  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();

  const fetchEvents = useCallback(async () => {
    try {
      showLoader();
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await events.getList({
        limit: itemsPerPage,
        offset,
        startDate: startDate?.toLocaleDateString('en-CA'),
        endDate: endDate?.toLocaleDateString('en-CA'),
      });

      setEventsData({
        events: response.events,
        totalPages: response.totalPages,
      });
    } catch (error) {
      showAlert('Failed to fetch events', 'error');
    } finally {
      hideLoader();
    }
  }, [currentPage, endDate, hideLoader, itemsPerPage, showAlert, showLoader, startDate]);

  const deleteEvent = useCallback(async (eventId: string) => {
    try {
      showLoader();
      await events.delete(eventId);
      await fetchEvents();
      showAlert('Event deleted successfully', 'success');
    } catch (error) {
      showAlert('Failed to delete event', 'error');
    } finally {
      hideLoader();
    }
  }, [fetchEvents, hideLoader, showAlert, showLoader]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  return {
    currentPage,
    eventsData,
    onPageChange,
    onDeleteEvent: deleteEvent,
    fetchEvents,
  };
};
